import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import WorkIcon from '@mui/icons-material/Work';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import useLocation from '../hooks/useLocation';

/**
 * Worker's discovery profile: availability toggle + skill + daily rate +
 * location. This is what nearby constructors see when searching.
 */
const WorkerProfileCard = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { capture, busy } = useLocation();

  const [skill, setSkill] = useState(user?.skill || '');
  const [rate, setRate] = useState(user?.dailyRate ? String(Math.round(user.dailyRate)) : '');
  const [available, setAvailable] = useState(user?.isAvailable !== false);
  const [toast, setToast] = useState('');

  const save = async (patch) => {
    const { data } = await api.patch('/api/users/me', patch);
    updateUser({ ...user, ...data });
  };

  const toggleAvailable = async (e) => {
    const next = e.target.checked;
    setAvailable(next);
    await save({ isAvailable: next });
    setToast(t('nearby.saved'));
  };

  const saveProfile = async () => {
    await save({ skill, dailyRate: rate ? Number(rate) : undefined });
    setToast(t('nearby.saved'));
  };

  const shareLocation = async () => {
    const loc = await capture();
    if (loc) setToast(t('nearby.locationSaved'));
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <WorkIcon color="primary" />
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {t('nearby.profileTitle')}
          </Typography>
          <Switch checked={available} onChange={toggleAvailable} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('nearby.availability')} — {t('nearby.availabilityHint')}
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label={t('worker.skill')}
            value={skill}
            onChange={(e) => setSkill(e.target.value)}
          />
          <TextField
            size="small"
            label={t('worker.dailyRate')}
            value={rate}
            onChange={(e) => setRate(e.target.value.replace(/[^\d]/g, ''))}
            type="tel"
            sx={{ maxWidth: 160 }}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={saveProfile}>
            {t('common.save')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MyLocationIcon />}
            disabled={busy}
            onClick={shareLocation}
          >
            {t('nearby.useLocation')}
          </Button>
        </Stack>
      </CardContent>
      <Snackbar
        open={!!toast}
        autoHideDuration={2000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};

export default WorkerProfileCard;
